import React from 'react';
import Icon from '../../../components/AppIcon';

const LocationMap = ({ selectedLocation, locations, onLocationSelect }) => {
  // Calculate center point - use selected location or center of all locations
  const calculateMapCenter = () => {
    if (selectedLocation) {
      return { 
        lat: selectedLocation?.coordinates?.lat, 
        lng: selectedLocation?.coordinates?.lng 
      };
    }
    
    if (locations?.length === 0) return { lat: 11.0168, lng: 76.9558 };
    
    const totalLat = locations?.reduce((sum, loc) => sum + loc?.coordinates?.lat, 0);
    const totalLng = locations?.reduce((sum, loc) => sum + loc?.coordinates?.lng, 0);
    
    return {
      lat: totalLat / locations?.length,
      lng: totalLng / locations?.length
    };
  };

  const mapCenter = calculateMapCenter();
  
  // Use proper Google Place embed when single location has mapEmbedUrl (same as contact page)
  const usePlaceEmbed = locations?.length === 1 && (selectedLocation || locations?.[0])?.mapEmbedUrl;
  const mapSrc = usePlaceEmbed 
    ? (selectedLocation || locations?.[0])?.mapEmbedUrl 
    : (() => {
        const getZoomLevel = () => selectedLocation ? 16 : 13;
        const createMarkers = () => locations?.map((location, index) => {
          const markerColor = location?.status === 'Open' ? 'green' : 'red';
          const markerLabel = String.fromCharCode(65 + index);
          return `&markers=color:${markerColor}%7Clabel:${markerLabel}%7C${location?.coordinates?.lat},${location?.coordinates?.lng}`;
        }).join('');
        return `https://www.google.com/maps?q=${mapCenter?.lat},${mapCenter?.lng}&z=${getZoomLevel()}&output=embed${createMarkers()}`;
      })();

  return (
    <div className="bg-card rounded-xl shadow-elevation-1 overflow-hidden h-full">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            Collection Points Map
          </h3>
          <div className="flex items-center space-x-3">
            {selectedLocation && (
              <button
                onClick={() => onLocationSelect(null)}
                className="text-xs bg-muted hover:bg-muted/80 text-muted-foreground px-3 py-1 rounded-full transition-colors flex items-center space-x-1"
              >
                <Icon name="Map" size={12} />
                <span>Show All</span>
              </button>
            )}
            <div className="flex items-center text-sm text-muted-foreground">
              <Icon name="MapPin" size={16} className="mr-1" />
              <span>{locations?.length} Locations</span>
            </div>
          </div>
        </div>
        
        {/* Location Legend */}
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-2 gap-1 sm:gap-2">
          {locations?.slice(0, 6)?.map((location, index) => {
            const markerLabel = String.fromCharCode(65 + index);
            const isSelected = selectedLocation?.id === location?.id;
            return (
              <div 
                key={location?.id}
                className={`p-1.5 sm:p-2 rounded-lg cursor-pointer transition-all ${
                  isSelected 
                    ? 'bg-primary/20 border-2 border-primary shadow-sm' 
                    : 'bg-muted/50 hover:bg-muted border border-transparent'
                }`}
                onClick={() => onLocationSelect(location)}
              >
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : location?.status === 'Open' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-red-500 text-white'
                  }`}>
                    {markerLabel}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium truncate ${
                      isSelected ? 'text-primary' : 'text-foreground'
                    }`}>
                      {location?.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate hidden sm:block">
                      {location?.status}
                    </p>
                  </div>
                  {isSelected && (
                    <Icon name="Check" size={10} className="text-primary flex-shrink-0 sm:w-3 sm:h-3" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {selectedLocation && (
          <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-primary/10 rounded-lg border border-primary/20">
            <p className="text-sm font-medium text-primary">
              {selectedLocation?.name}
            </p>
            <p className="text-xs text-primary/80 mt-1 line-clamp-2">
              {selectedLocation?.address}
            </p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-primary/70 truncate">
                {selectedLocation?.phone}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                selectedLocation?.status === 'Open' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {selectedLocation?.status}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="relative h-96 sm:h-[28rem] lg:h-[32rem] xl:h-full">
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title="Maayo Fabric Care Collection Points"
          referrerPolicy="no-referrer-when-downgrade"
          src={mapSrc}
          className="border-0"
        />
        
        {/* Map Controls Overlay */}
        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-card rounded-lg shadow-elevation-2 p-1 sm:p-2">
          <div className="flex flex-col gap-1 sm:gap-2">
            <button className="p-1.5 sm:p-2 hover:bg-muted rounded-md transition-smooth">
              <Icon name="Plus" size={12} className="sm:w-4 sm:h-4" />
            </button>
            <button className="p-1.5 sm:p-2 hover:bg-muted rounded-md transition-smooth">
              <Icon name="Minus" size={12} className="sm:w-4 sm:h-4" />
            </button>
            <button className="p-1.5 sm:p-2 hover:bg-muted rounded-md transition-smooth">
              <Icon name="Locate" size={12} className="sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 bg-card rounded-lg shadow-elevation-2 p-2 sm:p-3">
          <div className="space-y-1 sm:space-y-2">
            <div className="flex items-center gap-2 sm:gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                <span className="text-muted-foreground text-xs">Open</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full"></div>
                <span className="text-muted-foreground text-xs">Closed</span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Markers:</span> A-F
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationMap;